import { Link, NavLink, useNavigate } from "react-router-dom";
import { Crown, LayoutDashboard, LogOut, ExternalLink, Building2, TrendingUp, BarChart3, LayoutGrid } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-800", "Contact attempted": "bg-amber-100 text-amber-800",
  Contacted: "bg-indigo-100 text-indigo-800", Qualified: "bg-teal-100 text-teal-800",
  "Viewing required": "bg-purple-100 text-purple-800", "Offer made": "bg-emerald-100 text-emerald-800",
  Negotiating: "bg-cyan-100 text-cyan-800", "Under offer": "bg-lime-100 text-lime-800",
  Completed: "bg-green-200 text-green-900", "Not suitable": "bg-slate-200 text-slate-600",
  Nurture: "bg-orange-100 text-orange-800", Closed: "bg-slate-200 text-slate-500",
};
const PRIORITY_COLORS = { Hot: "bg-red-100 text-red-700", Warm: "bg-amber-100 text-amber-700", Cold: "bg-sky-100 text-sky-700" };
export const statusBadge = (s) => STATUS_COLORS[s] || "bg-slate-100 text-slate-700";
export const priorityBadge = (p) => PRIORITY_COLORS[p] || "bg-slate-100 text-slate-700";

export function AdminShell({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#091E16] text-slate-100">
      <header className="sticky top-0 z-40 bg-[#0F2E23]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/admin" className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-md bg-white/10"><Crown className="w-4.5 h-4.5" style={{ color: "#C59B27" }} /></span>
            <div className="leading-none">
              <div className="font-serif text-white text-base">Emerald Crown</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-brass">Website Admin</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {[["Home", "/admin", LayoutDashboard], ["Projects", "/admin/projects", Building2], ["Opportunities", "/admin/opportunities", TrendingUp], ["Analytics", "/admin/analytics", BarChart3]].map(([label, path, Icon]) => (
              <NavLink key={path} to={path} end={path === "/admin"} data-testid={`adminnav-${label.toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition ${isActive ? "bg-brass text-emerald-950 font-semibold" : "text-emerald-100/70 hover:bg-white/5"}`}>
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/crm" data-testid="admin-open-crm" className="flex items-center gap-1.5 text-xs font-semibold bg-brass hover:bg-brass-600 text-emerald-950 rounded-full px-3 py-1.5 transition"><LayoutGrid className="w-3.5 h-3.5" /> Open CRM</Link>
            <Link to="/" target="_blank" className="text-xs text-emerald-100/70 hover:text-white flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> View site</Link>
            <span className="text-sm text-emerald-100/80 hidden sm:block">{user?.name || user?.email}</span>
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <button onClick={doLogout} data-testid="admin-logout" className="text-emerald-100/70 hover:text-white"><LogOut className="w-4.5 h-4.5" /></button>
          </div>
        </div>
      </header>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {title && <div className="flex items-center gap-2 mb-6"><LayoutDashboard className="w-5 h-5 text-brass" /><h1 className="text-2xl font-serif text-white">{title}</h1></div>}
        {children}
      </div>
    </div>
  );
}
